import { useState } from 'react';
import { Button } from '~/components/ui/button';

/**
 * FRONT-2465 / FRONT-2601: renders a `continue-offer` signal — the one-tap
 * "Continue" button under a server-authored fallback reply (stuck, completed
 * without a write-up, replayed echo, or one of the two unsaved-file warnings).
 * A tap sends the part's own `prompt` through the chat hook exactly as if
 * typed ("continue" when the part carries none), and the button collapses
 * locally.
 *
 * When the part says `replacesText`, the boilerplate sentence is hidden (see
 * `buildOrderedContentSegments`) and this card's own label stands in for it;
 * the label outlives the button, so a past turn still says why it stopped.
 * When it does not (the two save warnings), the warning stays visible above
 * the button.
 *
 * The button follows the `QuickRepliesCard` visibility contract: only on the
 * last message (any later turn means the offer was answered or overtaken, and
 * it must not resurface on reload), only once the host message has stopped
 * streaming, and only where the host surface can send a message — surfaces
 * without a send function render no dead button.
 */
export function ContinueOfferCard({
  onSendMessage,
  isLastMessage,
  isStreaming,
  replacesText,
  prompt,
}: {
  onSendMessage?: (text: string) => void | Promise<void>;
  isLastMessage?: boolean;
  /** The host message is still streaming — render nothing until it settles. */
  isStreaming?: boolean;
  /**
   * FRONT-2601: the server's boilerplate sentence is hidden (see
   * `buildOrderedContentSegments`), so this card's own label stands in for it.
   * Missing (older persisted messages) means false.
   */
  replacesText?: boolean;
  /** What the button sends; missing (older persisted messages) means "continue". */
  prompt?: string;
}) {
  const [used, setUsed] = useState(false);

  // The label outlives the button on purpose: a past turn whose sentence is
  // hidden must still say why it stopped. Only the button follows the
  // last-message / streaming / can-send rules.
  const showButton = !used && !isStreaming && !!isLastMessage && !!onSendMessage;
  if (!replacesText && !showButton) return null;

  return (
    <div className="flex flex-col items-start gap-2 pt-1 max-w-[85%]">
      {replacesText && (
        <p className="text-sm text-muted-foreground" data-testid="continue-offer-label">
          EDDY paused before writing up the result.
        </p>
      )}
      {showButton && (
        <Button
          type="button"
          size="lg"
          variant="outline"
          data-testid="continue-offer"
          aria-label="Continue the unfinished task"
          onClick={() => {
            setUsed(true);
            void onSendMessage(prompt ?? 'continue');
          }}
        >
          Continue
        </Button>
      )}
    </div>
  );
}
